import React from "react"
import PropTypes from "prop-types"
import { orderBy, sumBy, isNaN, round } from "lodash"

// Utils
import { downloadScreenShotFile, getImgInPDF } from "Utils/screenshotPage"
import { getObjectToExport } from "Utils/widgetsUtils"
import { exportDatas } from "Utils/global"

// Components
import ExportMenu from "Components/ExportMenu"

// Styled component
import {
  MultiProgressBarContainer,
  ProgressBarContainer,
  MultiMainContainer,
  LegendContainer,
  MultiContainer,
  MainContainer,
  LegendLabel,
  MultiValue,
  MultiLabel,
  Square,
  Legend,
  Value,
  Label,
  Span,
  Bar,
} from "Components/chartsLibrary/ProgressBar/index.style"

const ProgressBar = ({
  enableExportMenu,
  displayInMultipleProgressBar,
  progressBarSecondLabelWeight,
  progressBarSecondLabelColor,
  progressBarSecondLabelSize,
  progressBarFirstLabelWeight,
  progressBarFirstLabelColor,
  progressBarFirstLabelSize,
  tooltipValueType,
  progressBarNumber,
  tooltipActivation,
  progressBarRadius,
  progressBarHeight,
  visibilityLegend,
  uniqueColorValue,
  legendPosition,
  colorSetTheme,
  labelPosition,
  disableLabel,
  showLabel,
  categoryData,
  uniqueColor,
  labelWeight,
  valueWeight,
  hideValues,
  labelColor,
  valueSize,
  dataChart,
  valueData,
  labelSize,
  typeValue,
  isMobile,
  showSpaceForThousand,
  widgetTitle,
  fieldsData,
  dataExportFunction,
}) => {
  var showLabelToUse = false
  if (showLabel !== undefined) showLabelToUse = showLabel
  else if (disableLabel !== undefined) {
    //there was an error in the code where disableLabel stands for showLabel
    //disableLabel => true means showLabel
    //disableLabel => false means hideLabel
    showLabelToUse = disableLabel
  }

  var isFirstPeriod = text => text.includes("Compared1")
  var isSecondPeriod = text => text.includes("Compared2")

  var showPeriod = false
  if (valueData && valueData.length >= 2) {
    var hasComp1 = false
    var hasComp2 = false
    valueData.forEach(aValueData => {
      if (isFirstPeriod(aValueData)) hasComp1 = true
      if (isSecondPeriod(aValueData)) hasComp2 = true
    })
    showPeriod = hasComp1 && hasComp2
  }

  const getTooltipTextContent = (tooltipValueType, category, value) => {
    if (tooltipValueType === "text") return category
    else if (tooltipValueType === "value") return value
    else return category + " : " + value
  }
  const getValue = (typeValue, data, value, currentValueData) => {
    if (typeValue === "value_and_percent") {
      var dataToShow = data?.[currentValueData]
      return dataToShow ? dataToShow : 0
    } else if (typeValue === "percent_and_value") return round(value)
    else return ""
  }

  const getValueOrPercentJSX = (typeValue, data, currentValueData, value, color) => {
    return (
      <>
        {getValue(typeValue, data, value, currentValueData)}
        {typeValue === "percent_and_value" && (
          <Span valueSize={valueSize} valueColor={uniqueColor ? uniqueColorValue : color}>
            %
          </Span>
        )}
        {(typeValue === "value_and_percent" || typeValue === "percent_and_value") && (
          <span style={{ margin: "0 0.5rem" }}>|</span>
        )}
        {!isNaN(value) && (
          <div>
            {typeValue === "percent" || typeValue === "value_and_percent"
              ? round(value)
              : typeValue === "value" || typeValue === "percent_and_value"
              ? getNumericalValue(data?.[currentValueData], showSpaceForThousand)
              : round(value)}
          </div>
        )}
        {(typeValue === "percent" || typeValue === "value_and_percent") && (
          <Span valueSize={valueSize} valueColor={uniqueColor ? uniqueColorValue : color}>
            %
          </Span>
        )}
      </>
    )
  }

  const getNumericalValue = (value, formatWithSpace) => {
    if (formatWithSpace) return value.toLocaleString()
    return value
  }

  const getLegendContainerJSX = (dataChart, valueData, categoryData, legendPosition) => {
    return (
      <LegendContainer legendPosition={legendPosition}>
        {orderBy(dataChart, valueData?.[0], "desc")?.map((data, index) => {
          const category = data?.[categoryData]
          const color = colorSetTheme?.[index]

          return (
            <Legend key={`${category}${index}`} legendPosition={legendPosition}>
              <Square color={color} />
              <LegendLabel>{category}</LegendLabel>
            </Legend>
          )
        })}
      </LegendContainer>
    )
  }

  const getLabel = (value, category) => {
    if (value >= 7.5) return category
    return category?.toString()?.slice(0, 2) + "."
  }

  // EXPORT MENU
  const exportMenuId = `exportMenu${Math.random()}`
  const ID = `progressBar${Math.random()}`

  const hideElement = element => {
    element.style.display = "none"
  }

  const exportPNG = () => {
    const title = widgetTitle || "progressBar"
    const exportMenu = document.getElementById(exportMenuId)
    hideElement(exportMenu)
    // take the screen and export
    downloadScreenShotFile(ID, title)
    // display again the menu
    setTimeout(() => exportMenu.removeAttribute("style"), 10) // ten milliseconds is invisible for the user
  }

  const getImgAndSaveAsPDF = () => {
    const title = widgetTitle || "progressBar"
    const exportMenu = document.getElementById(exportMenuId)
    hideElement(exportMenu)
    // take the screen and export
    getImgInPDF(ID, title)
    // display again the menu
    setTimeout(() => exportMenu.removeAttribute("style"), 10) // ten milliseconds is invisible for the user
  }

  const exportData = (fileType = "csv") => {
    if (dataChart.length) {
      const title = widgetTitle || "progressBar"
      dataExportFunction(fileType, title, valueData, dataChart)
    } else {
      alert("no datas to export")
    }
  }

  const exportMenuPosition = { top: "0px", right: "25px" }

  //RETURN
  if (displayInMultipleProgressBar) {
    //vertical
    return (
      <MultiMainContainer role="verticalModeMainContainer" id={ID}>
        {enableExportMenu && (
          <ExportMenu
            exportMenuPosition={exportMenuPosition}
            exportPNG={exportPNG}
            exportCSV={() => exportData("csv")}
            exportXLSX={() => exportData("xlsx")}
            exportPDF={() => exportData("pdf")}
            exportImgPDF={getImgAndSaveAsPDF}
            exportMenuId={exportMenuId}
          />
        )}
        {orderBy(dataChart, valueData?.[0], "desc")?.map((data, index) => {
          const color = colorSetTheme?.[index]
          const category = data?.[categoryData]

          return (
            index <= progressBarNumber - 1 && (
              <MultiContainer
                id="progress_bar"
                key={`${category}${index}`}
                legendPosition={legendPosition}
                isMobile={isMobile}
              >
                <>
                  {valueData.map((oneValueData, valueDataIndex) => {
                    const valueMax = sumBy(dataChart, oneValueData)
                    const value = round((data?.[oneValueData] * 100) / valueMax)
                    var valueToUse = isNaN(value) ? 0 : value
                    return (
                      <div
                        id="valueDataRow"
                        key={oneValueData}
                        style={{ display: "flex", flexDirection: "row", width: "100%" }}
                      >
                        {showLabelToUse && (
                          <MultiLabel
                            labelWeight={labelWeight}
                            labelSize={labelSize}
                            labelColor={uniqueColor ? uniqueColorValue : color}
                            isMobile={isMobile}
                          >
                            {valueDataIndex < 1 ? category : ""}
                          </MultiLabel>
                        )}
                        <MultiProgressBarContainer isMobile={isMobile}>
                          <Bar
                            role={`verticalBarSize${valueToUse}`}
                            height={progressBarHeight}
                            radius={progressBarRadius}
                            width={valueToUse}
                            color={color}
                          >
                            {tooltipActivation && (
                              <div className="tooltip">
                                <div className="tooltiptext">
                                  {getTooltipTextContent(tooltipValueType, category, valueToUse)}
                                </div>
                              </div>
                            )}
                          </Bar>
                          {showPeriod && (
                            <div role="periodIndicator" style={{ color: color }}>
                              {isFirstPeriod(oneValueData) ? "P1" : "P2"}
                            </div>
                          )}
                        </MultiProgressBarContainer>
                        {!hideValues && (
                          <MultiValue
                            valueWeight={valueWeight}
                            valueSize={valueSize}
                            valueColor={uniqueColor ? uniqueColorValue : color}
                            isMobile={isMobile}
                          >
                            {getValueOrPercentJSX(typeValue, data, oneValueData, valueToUse, color)}
                          </MultiValue>
                        )}
                      </div>
                    )
                  })}
                </>
              </MultiContainer>
            )
          )
        })}
      </MultiMainContainer>
    )
  } else {
    //horizontal
    return (
      <MainContainer id={ID} legendPosition={legendPosition} role="progressBarHorizontal">
        {enableExportMenu && (
          <ExportMenu
            exportMenuPosition={exportMenuPosition}
            exportPNG={exportPNG}
            exportCSV={() => exportData("csv")}
            exportXLSX={() => exportData("xlsx")}
            exportPDF={() => exportData("pdf")}
            exportImgPDF={getImgAndSaveAsPDF}
            exportMenuId={exportMenuId}
          />
        )}
        {visibilityLegend &&
          ["top", "left"].includes(legendPosition) &&
          getLegendContainerJSX(dataChart, valueData, categoryData, legendPosition)}
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          {valueData.map(oneValueData => {
            return (
              <ProgressBarContainer id="horizontalBarsContainer" key={oneValueData}>
                {showPeriod && (
                  <div
                    role="periodIndicator"
                    style={{
                      color: "black",
                      position: "absolute",
                      width: "50px",
                      height: "50px",
                    }}
                  >
                    {isFirstPeriod(oneValueData) ? "P1" : "P2"}
                  </div>
                )}
                {orderBy(dataChart, oneValueData, "desc")?.map((data, index) => {
                  const valueMax = sumBy(dataChart, oneValueData)
                  const value = round((data?.[oneValueData] * 100) / valueMax)
                  var valueToUse = isNaN(value) ? 0 : value
                  const category = data?.[categoryData]
                  const color = colorSetTheme?.[index]

                  if (data?.[oneValueData] === null) return <></>

                  return (
                    <Bar
                      id="horizontalBar"
                      key={category}
                      height={progressBarHeight}
                      radius={progressBarRadius}
                      width={valueToUse}
                      color={color}
                    >
                      {tooltipActivation && (
                        <div className="tooltip">
                          <div className="tooltiptext">
                            {getTooltipTextContent(tooltipValueType, category, valueToUse)}
                          </div>
                        </div>
                      )}
                      {showLabelToUse && (
                        <Label
                          checkLength={dataChart.length === 2}
                          labelPosition={labelPosition}
                          fontHeight={progressBarHeight}
                          labelWeight={labelWeight}
                          labelSize={labelSize}
                          labelColor={uniqueColor ? uniqueColorValue : color}
                          index={index}
                        >
                          {getLabel(valueToUse, category)}
                        </Label>
                      )}
                      {!hideValues && value > 5 && (
                        <Value
                          checkLength={dataChart.length === 2}
                          legendPosition={legendPosition}
                          labelPosition={labelPosition}
                          fontHeight={progressBarHeight}
                          valueWeight={valueWeight}
                          valueSize={valueSize}
                          valueColor={uniqueColor ? uniqueColorValue : color}
                          index={index}
                        >
                          {getValueOrPercentJSX(typeValue, data, oneValueData, valueToUse, color)}
                        </Value>
                      )}
                    </Bar>
                  )
                })}
              </ProgressBarContainer>
            )
          })}
        </div>
        {visibilityLegend &&
          ["bottom", "right"].includes(legendPosition) &&
          getLegendContainerJSX(dataChart, valueData, categoryData, legendPosition)}
      </MainContainer>
    )
  }
}

ProgressBar.propTypes = {
  enableExportMenu: PropTypes.bool,
  displayInMultipleProgressBar: PropTypes.bool,
  progressBarSecondLabelWeight: PropTypes.string,
  progressBarSecondLabelColor: PropTypes.string,
  progressBarSecondLabelSize: PropTypes.string,
  progressBarFirstLabelWeight: PropTypes.string,
  progressBarFirstLabelColor: PropTypes.string,
  progressBarFirstLabelSize: PropTypes.string,
  tooltipActivation: PropTypes.bool,
  progressBarNumber: PropTypes.number,
  progressBarRadius: PropTypes.number,
  progressBarHeight: PropTypes.number,
  uniqueColorValue: PropTypes.string,
  tooltipValueType: PropTypes.string,
  legendPosition: PropTypes.string,
  visibilityLegend: PropTypes.bool,
  labelPosition: PropTypes.string,
  labelWeight: PropTypes.string,
  uniqueColor: PropTypes.bool,
  disableLabel: PropTypes.bool,
  showLabel: PropTypes.bool,
  labelSize: PropTypes.string,
  hideValues: PropTypes.bool,
  typeValue: PropTypes.oneOf(["value_and_percent", "percent_and_value", "percent", "value"]),
  isMobile: PropTypes.bool,
  showSpaceForThousand: PropTypes.bool,
  fieldsData: PropTypes.array,
  dataExportFunction: PropTypes.func,
}

ProgressBar.defaultProps = {
  enableExportMenu: false,
  displayInMultipleProgressBar: false,
  progressBarSecondLabelWeight: "bold",
  progressBarSecondLabelColor: "#666",
  progressBarSecondLabelSize: "2rem",
  progressBarFirstLabelWeight: "bold",
  progressBarFirstLabelColor: "#000",
  progressBarFirstLabelSize: "2rem",
  tooltipActivation: false,
  progressBarNumber: 10,
  legendPosition: "bottom",
  visibilityLegend: false,
  labelPosition: "bottom",
  labelWeight: "normal",
  progressBarRadius: 20,
  progressBarHeight: 15,
  uniqueColorValue: "#000000",
  tooltipValueType: "text_and_value",
  typeValue: "percent",
  disableLabel: false,
  uniqueColor: false,
  labelColor: "#000",
  labelSize: "1rem",
  dataChart: [],
  categoryData: "",
  valueData: [],
  hideValues: true,
  labelWeight: "normal",
  labelSize: "1rem",
  isMobile: false,
  showSpaceForThousand: true,
  fieldsData: [],
  dataExportFunction: () => {},
}

export default ProgressBar
